/**
 * Priority Follow-up list — who to contact on a given date, oldest-touched first.
 * Due rule: tier 0 = terus · tier 1 = 1 hari · tier 2 = 3 hari · tier 3 = 7 hari
 * since the last follow-up. Done / Not Interested are excluded.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, ArrowUpCircle, CalendarClock, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { SALES_ADMINS } from '../constants/salesAdmins';

const API = process.env.REACT_APP_BACKEND_URL;

const TIER_LABEL = { 0: 'Belum', 1: 'Tier 1', 2: 'Tier 2', 3: 'Tier 3' };
const TIER_COLOR = { 0: '#6B7280', 1: '#3B82F6', 2: '#F59E0B', 3: '#EF4444' };

export default function ClientPriorityList({ ibId, todayISO, onChanged }) {
  const [date, setDate] = useState(todayISO);
  const [admin, setAdmin] = useState('all');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [pageSize, setPageSize] = useState(20);
  const [page, setPage] = useState(1);

  const fetchData = useCallback(async () => {
    if (!ibId) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const q = new URLSearchParams({ date });
      if (admin !== 'all') q.append('follow_up_by', admin);
      const res = await fetch(`${API}/api/clients/${ibId}/priority?${q}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setRows(data.clients || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [ibId, date, admin]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setPage(1); }, [date, admin, pageSize]);

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * pageSize;
  const pageRows = rows.slice(start, start + pageSize);

  const bump = async (row, tier) => {
    setBusyId(row.id);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/clients/${ibId}/${row.id}/follow-up-tier`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ follow_up_tier: tier }),
      });
      if (!res.ok) throw new Error();
      toast.success(`${row.name} → ${tier === 4 ? 'Done' : tier === 5 ? 'Not Interested' : TIER_LABEL[tier]}`);
      fetchData();
      onChanged?.();
    } catch {
      toast.error('Gagal update follow-up');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="p-3 space-y-3" data-testid="priority-list">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-md px-2 h-8">
          <CalendarClock size={12} className="text-[hsl(var(--text-muted))]" />
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="bg-transparent text-[10px] text-white outline-none"
            data-testid="priority-date"
          />
        </div>
        <Select value={admin} onValueChange={setAdmin}>
          <SelectTrigger className="w-[160px] h-8 text-[10px] bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]" data-testid="priority-admin-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Admin Sales</SelectItem>
            {SALES_ADMINS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
          <SelectTrigger className="w-[110px] h-8 text-[10px] bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]" data-testid="priority-page-size">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[20, 50, 100].map((n) => <SelectItem key={n} value={String(n)}>{n} / page</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-[10px] text-[hsl(var(--text-muted))]" data-testid="priority-count">
          {rows.length} client perlu follow-up pada {date} · paling lama tak disentuh di atas
        </span>
      </div>

      {loading ? (
        <div className="py-12 text-center"><Loader2 size={20} className="animate-spin text-[#E63946] mx-auto" /></div>
      ) : rows.length === 0 ? (
        <p className="py-12 text-center text-[11px] text-[hsl(var(--text-muted))] italic" data-testid="priority-empty">
          Tiada client perlu follow-up pada tarikh ni. Bagus!
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]" data-testid="priority-table">
            <thead>
              <tr className="bg-[hsl(var(--surface-elevated))] text-[9px] uppercase text-[hsl(var(--text-muted))]">
                <th className="py-2 px-3 text-left w-10">#</th>
                <th className="py-2 px-3 text-left">Client</th>
                <th className="py-2 px-3 text-left">Tier</th>
                <th className="py-2 px-3 text-left">Follow-up Terakhir</th>
                <th className="py-2 px-3 text-center">Hari Lepas</th>
                <th className="py-2 px-3 text-left">Follow Up By</th>
                <th className="py-2 px-3 text-left">Remark</th>
                <th className="py-2 px-3 text-right">Tindakan</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((r, i) => (
                <tr key={r.id} className="border-t border-[hsl(var(--border))]/40 hover:bg-[hsl(var(--surface-elevated))]" data-testid={`priority-row-${r.id}`}>
                  <td className="py-2 px-3 font-black font-mono text-[hsl(var(--text-muted))]">{start + i + 1}</td>
                  <td className="py-2 px-3">
                    <p className="font-semibold text-white">{r.name}</p>
                    <p className="text-[9px] text-[hsl(var(--text-muted))]">
                      {r.status === 'active' ? 'Active' : 'Expired'} · exp {r.expiry_date || '—'}
                    </p>
                  </td>
                  <td className="py-2 px-3">
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded border"
                      style={{ color: TIER_COLOR[r.follow_up_tier], borderColor: `${TIER_COLOR[r.follow_up_tier]}55`, background: `${TIER_COLOR[r.follow_up_tier]}18` }}>
                      {TIER_LABEL[r.follow_up_tier]}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-white">{r.last_follow_up_date || <span className="text-[#EF4444] font-semibold">Belum pernah</span>}</td>
                  <td className="py-2 px-3 text-center font-mono">
                    {r.days_since === null ? '—' : (
                      <span className={r.overdue_days > 0 ? 'text-[#EF4444] font-bold' : 'text-white'}>
                        {r.days_since}d
                        {r.overdue_days > 0 && <AlertTriangle size={9} className="inline-block ml-1" />}
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-3 text-[hsl(var(--text-muted))]">{r.follow_up_by || '—'}</td>
                  <td className="py-2 px-3 text-[hsl(var(--text-muted))] max-w-[180px]">
                    <div className="truncate" title={r.last_follow_up_remark || ''}>{r.last_follow_up_remark || '—'}</div>
                  </td>
                  <td className="py-2 px-3 text-right whitespace-nowrap">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-[9px] mr-1"
                      disabled={busyId === r.id || r.follow_up_tier >= 3}
                      onClick={() => bump(r, r.next_tier)}
                      data-testid={`priority-bump-${r.id}`}
                    >
                      {busyId === r.id ? <Loader2 size={10} className="animate-spin" /> : <ArrowUpCircle size={10} className="mr-1" />}
                      {r.follow_up_tier >= 3 ? 'Max Tier' : `→ ${TIER_LABEL[r.next_tier]}`}
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-[9px] mr-1 text-[#10B981]" disabled={busyId === r.id} onClick={() => bump(r, 4)} data-testid={`priority-done-${r.id}`}>Done</Button>
                    <Button size="sm" variant="outline" className="h-7 text-[9px] text-[hsl(var(--text-muted))]" disabled={busyId === r.id} onClick={() => bump(r, 5)} data-testid={`priority-notint-${r.id}`}>Not Int.</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-center justify-between gap-2 pt-3" data-testid="priority-pagination">
            <span className="text-[10px] text-[hsl(var(--text-muted))]">
              {rows.length === 0 ? 0 : start + 1}–{Math.min(start + pageSize, rows.length)} dari {rows.length}
            </span>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" className="h-7 text-[9px]" disabled={safePage <= 1}
                onClick={() => setPage(safePage - 1)} data-testid="priority-prev-page">Prev</Button>
              <span className="text-[10px] text-white font-mono" data-testid="priority-page-info">
                {safePage} / {totalPages}
              </span>
              <Button size="sm" variant="outline" className="h-7 text-[9px]" disabled={safePage >= totalPages}
                onClick={() => setPage(safePage + 1)} data-testid="priority-next-page">Next</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
